import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, TextField, List, ListItemButton,
  ListItemText, ListItemAvatar, Avatar, Typography, Box,
  InputAdornment, Divider, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { getPatients } from '../services/api';

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await getPatients({ search: query, limit: 12 });
        setResults(res.data?.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, [query]);

  const handleSelect = useCallback((patient) => {
    onClose();
    setQuery('');
    navigate(`/patients/${patient.id}`);
  }, [navigate, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          mt: { xs: 4, md: 8 },
          verticalAlign: 'top',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(2px)' } } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Buscar paciente por nombre, DNI o teléfono…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled', fontSize: 22 }} />
              </InputAdornment>
            ),
            disableUnderline: true,
            sx: {
              fontSize: '1rem',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
          variant="standard"
        />

        {query.length >= 2 && (
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {loading && (
              <Typography variant="body2" color="text.disabled" sx={{ px: 3, py: 2 }}>
                Buscando…
              </Typography>
            )}
            {!loading && results.length === 0 && (
              <Typography variant="body2" color="text.disabled" sx={{ px: 3, py: 2 }}>
                Sin resultados para "{query}"
              </Typography>
            )}
            {!loading && results.length > 0 && (
              <List dense disablePadding>
                {results.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i > 0 && <Divider component="li" />}
                    <ListItemButton onClick={() => handleSelect(p)} sx={{ px: 2.5, py: 1.25 }}>
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.dark', fontSize: '0.85rem' }}>
                          {p.name[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                            {p.dni && <Typography variant="caption" color="text.secondary">DNI {p.dni}</Typography>}
                            {p.phone && <Typography variant="caption" color="text.secondary">{p.phone}</Typography>}
                            {!p.is_active && <Chip label="Inactivo" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {query.length < 2 && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="caption" color="text.disabled">
              Escribí al menos 2 caracteres · <kbd style={{ background: '#f0f0f0', borderRadius: 3, padding: '1px 5px', fontSize: '0.7rem' }}>Esc</kbd> para cerrar
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
